import { isTesting } from '@/../utils/env'
import Config from '@/services/Config'

import BaseAddressProvider from './BaseAddressProvider'
import AddressComponent from '@/models/AddressComponent'

const DEFAULT_TEST_ADDRESS = new AddressComponent({
  formatted_address: '15 Bd Charles de Gaulle, 92700 Colombes, France',
  address_components: [
    {
      long_name: '15',
      short_name: '15',
      types: ['street_number']
    },
    {
      long_name: 'Bd Charles de Gaulle',
      short_name: 'Bd Charles de Gaulle',
      types: ['route']
    },
    {
      long_name: 'Colombes',
      short_name: 'Colombes',
      types: ['locality']
    },
    {
      long_name: 'France',
      short_name: 'FR',
      types: ['country']
    },
    {
      long_name: '92700',
      short_name: '92700',
      types: ['postal_code']
    }
  ],
  geometry: {
    location: {
      lat: () => 40,
      lng: () => 20
    }
  }
})

/**
 * See: https://developers.google.com/places/web-service/supported_types
 */
const POI_FILTER_TYPES = [
  'street_address',
  'route',
  'establishment',
  'premise'
]

/**
 * (regions) and (cities) collection types
 */
const REGION_TYPES = [
  'locality',
  'sublocality',
  'postal_code',
  'country',
  'administrative_area_level_1',
  'administrative_area_level_2'
]

const CITY_TYPES = [
  'locality',
  'administrative_area_level_3'
]

const REGION_FILTER_TYPES = [...REGION_TYPES, ...CITY_TYPES]

export default class GoogleAddressProvider extends BaseAddressProvider {
  /**
   * @type{Array<String>} defaultFilterTypes
   */
  defaultFilterTypes = [...POI_FILTER_TYPES, ...REGION_FILTER_TYPES]

  /**
   * @param {Array<String>} filterTypes
   */
  constructor (filterTypes = null) {
    super()
    this.filterTypes = filterTypes ?? this.defaultFilterTypes
  }

  /**
   * Returns a list of predictions according to a query
   * @method getPredictions
   * @param {string} query
   * @returns {Promise<Array>} result
   */
  getPredictions (query) {
    return new Promise(resolve => {
      /**
       * If we are in the testing environment, ignore the Google autocomplete thing and
       * return a predefined list of addresses for tesing purposes; since we don't want
       * to use Google API during tests.
       */
      if (isTesting) {
        resolve([
          {
            id: 'CHRONOTRUCK',
            description: 'Chronotruck, Bd Charles de Gaulle, Colombes, France',
            name: 'Chronotruck',
            types: [
              'establishment'
            ],
            provider: 'GoogleAddressProvider'
          }
        ])
        return false
      }

      if (typeof window.google === 'undefined' || typeof window.google.maps === 'undefined') {
        /**
         * TODO: Handle promise rejection
         */
        resolve([])
        return false
      }

      const autocompleteService = new window.google.maps.places.AutocompleteService()
      autocompleteService.getPlacePredictions({
        input: query
      }, (predictions, status) => {
        /**
         * See: https://developers.google.com/maps/documentation/javascript/reference/places-service#PlacesServiceStatus
         * @const status
         */
        if (status === 'ZERO_RESULTS') {
          resolve([])
        }

        if (status === 'OK') {
          const regionFilteredPredictions = predictions.filter(prediction => {
            return prediction.types.findIndex(p => REGION_FILTER_TYPES.includes(p)) !== -1
          })

          const poiFilteredPredictions = predictions.filter(prediction => {
            return prediction.types.findIndex(p => POI_FILTER_TYPES.includes(p)) !== -1
          })

          const filteredPredictions = [
            ...regionFilteredPredictions,
            ...poiFilteredPredictions
          ]

          const predictionResult = filteredPredictions.map(prediction => ({
            id: prediction.place_id,
            description: prediction.description,
            name: prediction.structured_formatting && prediction.structured_formatting.main_text
              ? prediction.structured_formatting.main_text
              : null,
            types: prediction.types,
            provider: 'GoogleAddressProvider'
          }))

          resolve(predictionResult)
        }
      })
    })
  }

  /**
   * Returns a detailed address item
   * @method getItem
   * @param {string} id
   * @returns {Promise<AddressComponent|Error>} result
   */
  getItem (id) {
    return new Promise((resolve, reject) => {
      /**
       * If we are in the testing environment, ignore the Google autocomplete thing and
       * return a predefined list of addresses for tesing purposes; since we don't want
       * to use Google API during tests.
       */
      if (isTesting) {
        resolve(DEFAULT_TEST_ADDRESS)
        return false
      }

      if (typeof window.google === 'undefined' || typeof window.google.maps === 'undefined') {
        return reject(new Error('Google Maps script is not injected.'))
      }

      const attributionElement = document.createElement('div')
      const service = new window.google.maps.places.PlacesService(attributionElement)
      const request = {
        placeId: id,
        fields: [
          'address_component',
          'geometry'
        ]
      }

      service.getDetails(request, (result, status) => {
        if (status === 'OK') {
          if (result) {
            resolve(new AddressComponent(result))
          } else {
            reject(new Error(status))
          }
        } else {
          reject(new Error(status))
        }
      })
    })
  }

  /**
   * @method geocode
   * @param {object} fields
   * @param {string} fields.city
   * @param {string} fields.address
   * @param {string} fields.country
   * @returns {Promise<any>}
   */
  geocode (fields) {
    const geocoder = new window.google.maps.Geocoder()
    return new Promise((resolve, reject) => {
      if (isTesting) {
        return resolve([
          DEFAULT_TEST_ADDRESS,
          DEFAULT_TEST_ADDRESS,
          DEFAULT_TEST_ADDRESS
        ])
      }

      geocoder.geocode({
        address: `${fields.address}, ${fields.postalCode} ${fields.city}, ${fields.country}`,
        componentRestrictions: {
          administrativeArea: fields.city,
          country: fields.country
        }
      }, (results, status) => {
        switch (status) {
          case 'OK':
            resolve(results.map(result => new AddressComponent(result)))
            break
          case 'NOT_FOUND':
          case 'ZERO_RESULTS':
            resolve([])
            break
          case 'INVALID_REQUEST':
          case 'OVER_QUERY_LIMIT':
          case 'REQUEST_DENIED':
          case 'UNKNOWN_ERROR':
            reject(new Error(`Could not geocode the address: ${status}`))
            break
        }
      })
    })
  }

  /**
   * Inject the Google Maps script to the page.
   * @method inject
   * @param {?any} [callback] - Function called whenever the script is fully loaded
   */
  inject (callback) {
    if ((window.google && window.google.maps) || window._google_maps_injected) {
      console.log('Google Maps script already injected.')
      return
    }

    const s = document.createElement('script')
    s.setAttribute('src', `https://maps.googleapis.com/maps/api/js?libraries=places&key=${Config.get('maps.id')}`)
    s.setAttribute('type', 'text/javascript')
    s.onload = callback
    document.body.appendChild(s)
    window._google_maps_injected = true
  }
}
